import { Layout } from '@components/Layout';
import { NestedSidebar } from '@components/SidebarV2';
import type { NestedSidebarItem } from '@components/SidebarV2';
import { Box } from '@mui/material';
import PermissionGate from '@components/upercut-permissions/PermissionGate';
import TalentJobApplications from '@modules/talents/TalentJobApplications';
import TalentPreview from '@modules/talents/components/TalentPreview';
import TalentEditDocument from '@modules/talents/TalentEditDocument';
import TalentFiles from '@modules/talents/TalentFiles';
import TalentNotes from '@modules/talents/TalentNotes';
import CardProfile from '@modules/talents/components/CardProfile';
import ProfileSkeleton from '@modules/talents/components/ProfileSkeleton';
import JobOfferSuggestionDialog from '@modules/JobOffers/JobOfferSuggestionDialog';
import { useQuery } from '@apollo/client';
import { GET_TALENT } from '@gql/talents';
import { GET_JOB_OFFER_APPLICATION_UPERCUT } from '@gql/jobs';
import { useMemo, useState } from 'react';
import { Stack } from '@mui/system';
import { usePageSize } from '@hooks/usePageSize';
import { useGetJobApplications } from '@modules/jobApplications/hooks/useGetJobApplications';
import { defaultJobApplicationFilters } from '@modules/jobApplications/JobApplicationFilter';
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';
import TalentFavoritesModule from './TalentFavoritesModule';

/**
 * Talent Favorites Page
 * Dedicated page for managing all favorite talents with filtering, sorting, and bulk operations
 *
 * User Story 4: Favorites Management Page
 * - Navigate to /talent-favorites
 * - View paginated favorites list
 * - Filter by type/priority
 * - Edit metadata and remove favorites
 */
const TalentFavoritesPage = () => {
    return (
        <Layout
            breadcrumbs={[
                { label: 'Tableau de bord', href: '/', value: 'dashboard' },
                { label: 'Mes Favoris', href: '/talent-favorites', value: 'talent-favorites' },
                { label: 'Détail du Talent', href: '/talent-favorites/:id/detail', value: 'detail' },
                { label: 'Candidatures du Talent', href: '/talent-favorites/:id/applications', value: 'applications' },
                { label: 'Documents du Talent', href: '/talent-favorites/:id/documents', value: 'documents' },
                { label: 'Fichiers du Talent', href: '/talent-favorites/:id/files', value: 'files' },
                { label: 'Notes du Talent', href: '/talent-favorites/:id/notes', value: 'notes' },
            ]}
            heading="Mes Talents Favoris"
        >
            <Routes>
                <Route
                    path="/"
                    element={
                        <PermissionGate permission="talents:read">
                            <TalentFavoritesModule />
                        </PermissionGate>
                    }
                />
                <Route
                    path="/:id"
                    element={<TalentFavoriteDetailsLayout />}
                >
                    <Route
                        index
                        element={<Navigate to="detail" replace />}
                    />
                    <Route
                        path="detail"
                        element={
                            <PermissionGate permission="talents:read">
                                <TalentPreview />
                            </PermissionGate>
                        }
                    />
                    <Route
                        path="applications"
                        element={
                            <PermissionGate permission="job-applications:read">
                                <TalentJobApplications />
                            </PermissionGate>
                        }
                    />
                    <Route
                        path="documents"
                        element={
                            <PermissionGate permission="talents:read">
                                <TalentEditDocument />
                            </PermissionGate>
                        }
                    />
                    <Route
                        path="files"
                        element={
                            <PermissionGate permission="talents:read">
                                <TalentFiles />
                            </PermissionGate>
                        }
                    />
                    <Route
                        path="notes"
                        element={
                            <PermissionGate permission="talents:read">
                                <TalentNotes />
                            </PermissionGate>
                        }
                    />
                </Route>
                <Route
                    path="/:id/*"
                    element={<TalentFavoriteIdFallbackRedirect />}
                />
            </Routes>
        </Layout>
    );
};

const TalentFavoriteIdFallbackRedirect = () => {
    const { id } = useParams<{ id: string }>();

    if (!id) {
        return <Navigate to="/talent-favorites" replace />;
    }

    return <Navigate to={`/talent-favorites/${id}/detail`} replace />;
};

const TalentFavoriteDetailsLayout = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const isDetailPage = location.pathname.endsWith('/detail');
    const { pageSize } = usePageSize({
        defaultPageSize: 12,
        storageKey: 'talent-favorite-details-page-size',
    });

    const [showSuggestDialog, setShowSuggestDialog] = useState(false);
    const [searchParams, setSearchParams] = useState({
        pagination: {
            page: 1,
            limit: pageSize,
        },
        sortBy: { field: 'createdDate', order: -1 },
    });

    const { data: talentData, loading: loadingTalentData } = useQuery(GET_TALENT, {
        variables: {
            talentId: id,
        },
        skip: !id,
    });

    const talentDetailData = useMemo(() => {
        return talentData?.getTalent;
    }, [talentData]);

    const { totalDocs: totalJobApplications } = useGetJobApplications(
        '',
        {
            ...defaultJobApplicationFilters,
            talentId: id ? [id] : [],
        },
        { field: 'createdDate', order: -1 },
        0,
        1
    );

    const nestedItems: NestedSidebarItem[] = useMemo(
        () => [
            { id: 'detail', label: 'Détail', icon: 'fi-rr-user', path: `/talent-favorites/${id}/detail` },
            { id: 'applications', label: 'Candidatures', icon: 'fi-br-inbox', path: `/talent-favorites/${id}/applications` },
            { id: 'documents', label: 'Documents', icon: 'fi-br-document', path: `/talent-favorites/${id}/documents` },
            { id: 'files', label: 'Fichiers', icon: 'fi-br-clip', path: `/talent-favorites/${id}/files` },
            { id: 'notes', label: 'Notes', icon: 'fi-br-book-alt', path: `/talent-favorites/${id}/notes` },
        ],
        [id],
    );

    if (loadingTalentData) {
        return (
            <Stack sx={{ pt: 0, flex: 1, minHeight: '100%', gap: 3 }}>
                <ProfileSkeleton />
            </Stack>
        );
    }

    return (
        <Box sx={{ display: 'flex', flex: 1, minHeight: '100%', mx: '-24px' }}>
            <NestedSidebar items={nestedItems} title="Favori" />
            <Stack flex={1} width={0} pt={0} minHeight="100%">
                {!isDetailPage && talentDetailData && (
                    <>
                        <Stack sx={{ gap: 3, mb: 3, px: 3 }}>
                            <CardProfile
                                onAssignJobClick={setShowSuggestDialog}
                                talent={{
                                    ...talentDetailData,
                                    totalJobApplications: totalJobApplications ?? 0,
                                }}
                            />
                        </Stack>
                        <JobOfferSuggestionDialog
                            open={showSuggestDialog}
                            setOpen={setShowSuggestDialog}
                            talentIds={[talentDetailData._id]}
                            refetchQueries={[
                                {
                                    query: GET_JOB_OFFER_APPLICATION_UPERCUT,
                                    variables: {
                                        pagination: searchParams.pagination,
                                        talentId: id,
                                        sortBy: searchParams.sortBy,
                                    },
                                },
                            ]}
                        />
                    </>
                )}
                <Box sx={{ flex: 1, pb: 3, display: 'flex', px: '24px', minWidth: 0 }}>
                    <Outlet context={{ searchParams, setSearchParams }} />
                </Box>
            </Stack>
        </Box>
    );
};

export default TalentFavoritesPage;
